// Review step — final wizard step for every part type.
//
// Read-only summary of everything captured in the prior steps. Organized
// into sections keyed off the part type so the user sees back what they
// just entered. The big "Save part" action lives on the wizard's action
// row (not here) — this component is purely presentational.

import type { StepProps } from '../stepTypes';
import type { PartType } from '@/shared/crmTypes';
import { PART_TYPE_META } from '@/components/PartTypeBadge';
import { calcCollarCutTime, calcCollarNesting, COLLAR_DEFAULTS } from '@/shared/collarMath';

export function ReviewStep({ part }: StepProps) {
  const meta = PART_TYPE_META[part.type];

  return (
    <div className="space-y-4">
      <div className="bg-bg/50 border border-line rounded p-3">
        <div className="text-[10px] font-semibold text-text-mute uppercase tracking-wide mb-1">
          Part summary
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-base font-bold text-navy font-mono">
            {part.number || <span className="text-text-mute italic">unset</span>}
          </span>
          <span className="text-sm text-text-mute">·</span>
          <span className="text-sm">{part.description || <span className="text-text-mute italic">no description</span>}</span>
        </div>
        <div className="text-xs text-text-mute mt-1">
          {meta.icon} {meta.label}
        </div>
      </div>

      {/* Type-specific review blocks */}
      {part.type === 'machined' && <MachinedReview part={part} />}
      {part.type === 'cut_blank' && <CutBlankReview part={part} />}
      {part.type === 'weldment' && <WeldmentReview part={part} />}
      {part.type === 'hybrid' && <HybridReview part={part} />}
      {part.type === 'purchased' && <PurchasedReview part={part} />}
      {part.type === 'collar' && <CollarReview part={part} />}

      {/* Notes always shown if present */}
      {part.notes && (
        <ReviewSection title="Notes">
          <pre className="whitespace-pre-wrap font-sans text-sm">{part.notes}</pre>
        </ReviewSection>
      )}

      <div className="bg-green/5 border-l-4 border-green rounded-r px-3 py-2 text-sm">
        <span aria-hidden>✅</span>{' '}
        Click <span className="font-semibold">Save part</span> below to write this part to
        the parts library. It can be referenced from any quote.
      </div>
    </div>
  );
}

// ───────────────────────── per-type review blocks ────────────────────────

function MachinedReview({ part }: { part: StepProps['part'] }) {
  return (
    <>
      <MaterialReview part={part} />
      <SetupsReview part={part} />
      <FinishingReview part={part} />
    </>
  );
}

function CutBlankReview({ part }: { part: StepProps['part'] }) {
  return (
    <>
      <MaterialReview part={part} label="Sheet stock" />
      <DxfReview part={part} />
      <FinishingReview part={part} />
    </>
  );
}

function WeldmentReview({ part }: { part: StepProps['part'] }) {
  const subs = part.weldment?.subParts ?? [];
  const ops = part.weldment?.weldOps ?? [];
  return (
    <>
      <ReviewSection title={`Sub-parts (${subs.length})`}>
        {subs.length === 0 ? (
          <Empty />
        ) : (
          <ul className="text-sm space-y-1">
            {subs.map((s, i) => {
              const qty = (s as unknown as { _qtyInAssembly?: number })._qtyInAssembly;
              return (
                <li key={i} className="flex justify-between">
                  <span className="font-mono">{s.number || <em className="italic">unnamed</em>}</span>
                  <span className="text-text-mute">qty {qty ?? 1}</span>
                </li>
              );
            })}
          </ul>
        )}
      </ReviewSection>
      <ReviewSection title={`Weld ops (${ops.length})`}>
        {ops.length === 0 ? (
          <Empty />
        ) : (
          <ul className="text-sm space-y-1">
            {ops.map((o, i) => (
              <li key={i} className="flex justify-between gap-2">
                <span>
                  <span className="font-semibold">{o.method}</span>
                  {o.electrodeType ? ` · ${o.electrodeType}` : ''}
                </span>
                <span className="text-text-mute font-mono">{o.hrs ?? 0}h · ${o.fillerCost ?? 0}</span>
              </li>
            ))}
          </ul>
        )}
      </ReviewSection>
      <FinishingReview part={part} />
    </>
  );
}

function HybridReview({ part }: { part: StepProps['part'] }) {
  return (
    <>
      <MaterialReview part={part} label="Sheet stock" />
      <DxfReview part={part} />
      <ReviewSection title="Secondary ops">
        {part.notes ? (
          <pre className="whitespace-pre-wrap font-sans text-sm">{part.notes}</pre>
        ) : (
          <Empty />
        )}
      </ReviewSection>
      <FinishingReview part={part} />
    </>
  );
}

function CollarReview({ part }: { part: StepProps['part'] }) {
  const collar = part.collar;
  if (!collar) {
    return (
      <ReviewSection title="Sheet">
        <Empty />
      </ReviewSection>
    );
  }
  const sheetLen = collar.sheetLen ?? COLLAR_DEFAULTS.sheetLen;
  const sheetWid = collar.sheetWid ?? COLLAR_DEFAULTS.sheetWid;
  const spacing = collar.spacing ?? COLLAR_DEFAULTS.spacing;
  const feedIpm = collar.feedIpm ?? COLLAR_DEFAULTS.feedIpm;
  const doc = collar.doc ?? COLLAR_DEFAULTS.doc;
  const nest = calcCollarNesting(collar.outsideLen, collar.outsideWid, sheetLen, sheetWid, spacing);
  // Same 0.5" thickness fallback as the cut step until the Material step exposes thickness.
  const t = calcCollarCutTime({
    outsideLen: collar.outsideLen,
    outsideWid: collar.outsideWid,
    frameWidth: collar.frameWidth,
    feedIpm,
    doc,
    materialThickness: 0.5,
  });
  const qty = part.qtyTiers?.[0] ?? 1;
  const sheetsNeeded = nest.count > 0 ? Math.ceil(qty / nest.count) : 0;
  return (
    <>
      <MaterialReview part={part} label="Sheet stock" />
      <ReviewSection title="Geometry">
        <div className="text-sm grid grid-cols-2 gap-y-1 gap-x-4">
          <Kv k="Outside">{collar.outsideLen}" × {collar.outsideWid}"</Kv>
          <Kv k="Frame width">{collar.frameWidth}"</Kv>
          <Kv k="Sheet">{sheetLen}" × {sheetWid}"</Kv>
          <Kv k="Spacing">{spacing}"</Kv>
        </div>
      </ReviewSection>
      <ReviewSection title="Nesting + cut time">
        <div className="text-sm grid grid-cols-2 gap-y-1 gap-x-4">
          <Kv k="Parts / sheet">{nest.count} ({nest.cols}×{nest.rows}{nest.rotated ? ', rotated' : ''})</Kv>
          <Kv k={`Sheets for qty ${qty}`}>{sheetsNeeded}</Kv>
          <Kv k="Passes">{t.passes}</Kv>
          <Kv k="Cut / part">{t.cutCombinedPerPart.toFixed(2)} min</Kv>
        </div>
      </ReviewSection>
      <FinishingReview part={part} />
    </>
  );
}

function PurchasedReview({ part }: { part: StepProps['part'] }) {
  const ps = part.purchasedSource;
  const mc = part.materialCost;
  return (
    <>
      <ReviewSection title="Source">
        {!ps ? (
          <Empty />
        ) : (
          <div className="text-sm grid grid-cols-2 gap-y-1 gap-x-4">
            <Kv k="Vendor">{ps.vendor}</Kv>
            <Kv k="SKU">{ps.sku || '—'}</Kv>
            <Kv k="URL">{ps.url ? <a href={ps.url} target="_blank" rel="noreferrer" className="text-orange hover:underline break-all">{ps.url}</a> : '—'}</Kv>
            <Kv k="Lead time">{ps.leadTime || '—'}</Kv>
            <Kv k="Pack size">{mc?.packSize ?? '—'}</Kv>
            <Kv k="Per-pack">${mc?.perPack ?? '—'}</Kv>
          </div>
        )}
      </ReviewSection>
      <ReviewSection title="Markup">
        <Kv k="Markup %">{ps?.markupPct ?? '—'}%</Kv>
        <Kv k="Declared value adj.">${part.setupCost ?? 0}</Kv>
      </ReviewSection>
    </>
  );
}

// ───────────────────────── shared block fragments ────────────────────────

function MaterialReview({ part, label = 'Material' }: { part: StepProps['part']; label?: string }) {
  const mc = part.materialCost;
  return (
    <ReviewSection title={label}>
      {!mc ? (
        <Empty />
      ) : (
        <div className="text-sm grid grid-cols-2 gap-y-1 gap-x-4">
          <Kv k="Material">{mc.vendorPartNum || '—'}</Kv>
          <Kv k="Per-bar / sheet">${mc.perBar ?? '—'}</Kv>
          <Kv k="Vendor">{mc.vendorId || '—'}</Kv>
          <Kv k="Snapshot src">{mc.snapshotSource || '—'}</Kv>
        </div>
      )}
    </ReviewSection>
  );
}

function SetupsReview({ part }: { part: StepProps['part'] }) {
  const machines = part.machines ?? [];
  return (
    <ReviewSection title={`Setups (${machines.length})`}>
      {machines.length === 0 ? (
        <Empty />
      ) : (
        <ul className="text-sm space-y-1">
          {machines.map((m, i) => (
            <li key={i} className="flex justify-between gap-2">
              <span className="truncate">{m.machine}</span>
              <span className="text-text-mute font-mono whitespace-nowrap">
                setup {m.setupHrs ?? 0}h · run {Math.round((m.hrsPerTier?.[0] ?? 0) * 60)} min/pc
              </span>
            </li>
          ))}
        </ul>
      )}
    </ReviewSection>
  );
}

function DxfReview({ part }: { part: StepProps['part'] }) {
  const dxfs = part.dxfFiles ?? [];
  return (
    <ReviewSection title={`DXF entries (${dxfs.length})`}>
      {dxfs.length === 0 ? (
        <Empty />
      ) : (
        <ul className="text-sm space-y-1">
          {dxfs.map((d, i) => (
            <li key={i} className="flex justify-between gap-2">
              <span className="truncate">
                <span className="font-mono">{d.filename || <em className="italic">unnamed</em>}</span>
                {d.vendor ? <span className="text-text-mute"> · {d.vendor}</span> : null}
              </span>
              <span className="text-text-mute font-mono whitespace-nowrap">
                ${d.setupCost ?? 0} setup · ${d.perPieceCost ?? 0}/pc
              </span>
            </li>
          ))}
        </ul>
      )}
    </ReviewSection>
  );
}

function FinishingReview({ part }: { part: StepProps['part'] }) {
  const svcs = part.outsideServices ?? [];
  return (
    <ReviewSection title={`Finishing services (${svcs.length})`}>
      {svcs.length === 0 ? (
        <Empty />
      ) : (
        <ul className="text-sm space-y-1">
          {svcs.map((s, i) => (
            <li key={i} className="flex justify-between gap-2">
              <span>
                <span className="font-semibold">{s.type}</span>
                {s.vendor ? <span className="text-text-mute"> · {s.vendor}</span> : null}
              </span>
              <span className="text-text-mute font-mono whitespace-nowrap">
                ${s.setupCost ?? 0} setup · ${s.perPieceCost ?? 0}/pc
              </span>
            </li>
          ))}
        </ul>
      )}
    </ReviewSection>
  );
}

// ───────────────────────── tiny shared bits ────────────────────────────

function ReviewSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="border border-line rounded p-3">
      <div className="text-[10px] font-semibold text-text-mute uppercase tracking-wide mb-2">
        {title}
      </div>
      {children}
    </div>
  );
}

function Empty() {
  return <div className="text-sm text-text-mute italic">(nothing entered)</div>;
}

function Kv({ k, children }: { k: string; children: React.ReactNode }) {
  return (
    <div className="text-sm">
      <span className="text-text-mute mr-2">{k}:</span>
      <span>{children}</span>
    </div>
  );
}

// Suppress noUnusedParameters / noUnusedLocals false-positives — the
// internal review components accept the typed Part draft via destructuring.
// Re-export types so TS retains them in the symbol table.
export type { PartType };
